import React, { useMemo, useState } from "react";
import { useLocale } from "./contexts/LocaleContext";
import { sanitizeInput } from "./utils/sanitize";
import { authenticatedFetch } from "./config/api";
import SaveButton from "./components/SaveButton";
import { Check, Play, Save, Trash2 } from "lucide-react";

/**
 * InvoiceNumberTemplate — интерактивный компонент для настройки кастомной нумерации инвойсов.
 * Позволяет создавать и управлять пресетами шаблонов нумерации.
 */

type Locale = "en" | "ru" | "cs";

type LocalizedStrings = {
  title: string;
  templateLabel: string;
  invalidTokensPrefix: string;
  quickTokens: string;
  presets: string;
  presetMonthSeq4: string;
  presetYYMMSeq3: string;
  presetDDMMYYYYSeq: string;
  presetYearMonthDaySeq5: string;
  myPresets: string;
  noCustomPresets: string;
  customerCodeLabel: string;
  projectCodeLabel: string;
  applyPreset: string;
  savePreset: string;
  presetSaved: string;
  presetApplied: string;
  deletePresetTitle: string;
  deletePresetConfirm: string;
  cancel: string;
  delete: string;
  previewTitle: string;
  deletePresetTooltip: string;
};

const LOCALIZED_STRINGS: Record<Locale, LocalizedStrings> = {
  ru: {
    title: "Настройка нумерации счетов-фактур",
    templateLabel: "Шаблон нумерации нынешней фактуры",
    invalidTokensPrefix: "Неподдерживаемые токены",
    quickTokens: "Быстрые токены",
    presets: "Пресеты",
    presetMonthSeq4: "Месяц + seq(4)",
    presetYYMMSeq3: "YYMM + seq(3)",
    presetDDMMYYYYSeq: "DDMMYYYY + seq",
    presetYearMonthDaySeq5: "Год/месяц/день + seq(5)",
    myPresets: "Мои пресеты",
    noCustomPresets: "Нет сохраненных пресетов",
    customerCodeLabel: "Код клиента {customer}",
    projectCodeLabel: "Код проекта {project}",
    applyPreset: "Применить пресет",
    savePreset: "Сохранить пресет",
    presetSaved: "Пресет сохранён",
    presetApplied: "Пресет применён",
    deletePresetTitle: "Удалить пресет?",
    deletePresetConfirm:
      "Вы уверены, что хотите удалить этот пресет? Это действие безвозвратное.",
    cancel: "Отмена",
    delete: "Удалить",
    previewTitle: "Превью (следующие 3 номера)",
    deletePresetTooltip: "Удалить пресет",
  },
  en: {
    title: "Invoice numbering settings",
    templateLabel: "Current invoice numbering template",
    invalidTokensPrefix: "Unsupported tokens",
    quickTokens: "Quick tokens",
    presets: "Presets",
    presetMonthSeq4: "Month + seq(4)",
    presetYYMMSeq3: "YYMM + seq(3)",
    presetDDMMYYYYSeq: "DDMMYYYY + seq",
    presetYearMonthDaySeq5: "Year/month/day + seq(5)",
    myPresets: "My presets",
    noCustomPresets: "No saved presets",
    customerCodeLabel: "Customer code {customer}",
    projectCodeLabel: "Project code {project}",
    applyPreset: "Apply preset",
    savePreset: "Save preset",
    presetSaved: "Preset saved",
    presetApplied: "Preset applied",
    deletePresetTitle: "Delete preset?",
    deletePresetConfirm:
      "Are you sure you want to delete this preset? This action cannot be undone.",
    cancel: "Cancel",
    delete: "Delete",
    previewTitle: "Preview (next 3 numbers)",
    deletePresetTooltip: "Delete preset",
  },
  cs: {
    title: "Nastavení číslování faktur",
    templateLabel: "Šablona číslování aktuální faktury",
    invalidTokensPrefix: "Nepodporované tokeny",
    quickTokens: "Rychlé tokeny",
    presets: "Předvolby",
    presetMonthSeq4: "Měsíc + seq(4)",
    presetYYMMSeq3: "YYMM + seq(3)",
    presetDDMMYYYYSeq: "DDMMYYYY + seq",
    presetYearMonthDaySeq5: "Rok/měsíc/den + seq(5)",
    myPresets: "Moje předvolby",
    noCustomPresets: "Žádné uložené předvolby",
    customerCodeLabel: "Kód zákazníka {customer}",
    projectCodeLabel: "Kód projektu {project}",
    applyPreset: "Použít předvolbu",
    savePreset: "Uložit předvolbu",
    presetSaved: "Předvolba uložena",
    presetApplied: "Předvolba použita",
    deletePresetTitle: "Smazat předvolbu?",
    deletePresetConfirm:
      "Opravdu chcete smazat tuto předvolbu? Tato akce je nevratná.",
    cancel: "Zrušit",
    delete: "Smazat",
    previewTitle: "Náhled (další 3 čísla)",
    deletePresetTooltip: "Smazat předvolbu",
  },
};

// Базовый список поддерживаемых токенов
const BASE_TOKENS = ["{YYYY}", "{YY}", "{MM}", "{DD}", "{seq}"];

// Помощь по токенам с локализацией описаний
const TOKEN_HELP_BY_LOCALE: Record<Locale, { token: string; desc: string }[]> =
  {
    ru: [
      { token: "{YYYY}", desc: "Полный год (2025)" },
      { token: "{YY}", desc: "Год (две цифры)" },
      { token: "{MM}", desc: "Месяц (01-12)" },
      { token: "{DD}", desc: "День (01-31)" },
      { token: "{seq}", desc: "Счётчик без паддинга" },
      { token: "{seq:4}", desc: "Счётчик с нулями до 4 цифр" },
    ],
    en: [
      { token: "{YYYY}", desc: "Full year (2025)" },
      { token: "{YY}", desc: "Year (two digits)" },
      { token: "{MM}", desc: "Month (01-12)" },
      { token: "{DD}", desc: "Day (01-31)" },
      { token: "{seq}", desc: "Counter without padding" },
      { token: "{seq:4}", desc: "Counter padded to 4 digits" },
    ],
    cs: [
      { token: "{YYYY}", desc: "Celý rok (2025)" },
      { token: "{YY}", desc: "Rok (dvě číslice)" },
      { token: "{MM}", desc: "Měsíc (01-12)" },
      { token: "{DD}", desc: "Den (01-31)" },
      { token: "{seq}", desc: "Čítač bez doplnění nul" },
      { token: "{seq:4}", desc: "Čítač doplněný na 4 číslice" },
    ],
  };

type Preset = {
  name: string;
  value: string;
  isFixed?: boolean;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: string, scope: string, previewFirst: string) => void;
  onApply?: (template: string, scope: string, previewFirst: string) => void; // применить к текущей фактуре без сохранения
  customPresets: Preset[];
  onDeletePreset?: (template: string) => void;
  currentCustomer?: string;
  currentProject?: string;
  zIndex?: number;
};

function pad(num: number, size: number) {
  const s = String(num);
  if (!Number.isFinite(size) || size <= 0) return s;
  return s.length >= size ? s : "0".repeat(size - s.length) + s;
}

function two(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

// Рендер шаблона без инкремента счётчика
function renderTemplate(
  template: string,
  vars: {
    date: Date;
    customer?: string;
    project?: string;
    seq: number;
  }
) {
  const d = vars.date;
  const map: Record<string, string> = {
    "{YYYY}": String(d.getFullYear()),
    "{YY}": String(d.getFullYear()).slice(-2),
    "{MM}": two(d.getMonth() + 1),
    "{DD}": two(d.getDate()),
    "{customer}": (vars.customer || "").trim().toUpperCase(),
    "{project}": (vars.project || "").trim().toUpperCase(),
    "{seq}": String(vars.seq),
  };

  // Сначала обрабатываем {seq:pad}
  let out = template.replace(/\{seq:(\d+)\}/g, (_, p1) =>
    pad(vars.seq, parseInt(p1, 10))
  );

  // Затем обычные токены
  for (const k of Object.keys(map)) {
    out = out.split(k).join(map[k]);
  }
  return out;
}

export default function InvoiceNumberTemplate({
  isOpen,
  onClose,
  onSave,
  onApply,
  customPresets = [],
  onDeletePreset,
  currentCustomer = "",
  currentProject = "",
  zIndex = 50,
}: Props) {
  const { locale } = useLocale();
  const texts = LOCALIZED_STRINGS[locale] ?? LOCALIZED_STRINGS.en;
  const TOKEN_HELP = TOKEN_HELP_BY_LOCALE[locale] ?? TOKEN_HELP_BY_LOCALE.en;

  // Фиксированные пресеты (нельзя удалить)
  const FIXED_PRESETS = useMemo(
    () => [
      {
        name: texts.presetMonthSeq4,
        value: "INV-{YYYY}-{MM}-{seq:4}",
        isFixed: true,
      },
      { name: texts.presetYYMMSeq3, value: "{YY}{MM}-{seq:3}", isFixed: true },
      {
        name: texts.presetDDMMYYYYSeq,
        value: "{DD}{MM}{YYYY}-{seq}",
        isFixed: true,
      },
      {
        name: texts.presetYearMonthDaySeq5,
        value: "INV/{YYYY}/{MM}/{DD}/{seq:5}",
        isFixed: true,
      },
    ],
    [texts]
  );

  const [template, setTemplate] = useState("INV-{YYYY}-{MM}-{seq:4}");
  const [customer, setCustomer] = useState(currentCustomer);
  const [project, setProject] = useState(currentProject);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null
  );

  // Сброс состояния подтверждения удаления при закрытии основной модалки
  React.useEffect(() => {
    if (!isOpen) {
      setShowDeleteConfirm(null);
    }
  }, [isOpen]);

  // Сегодняшняя дата для демо
  const today = useMemo(() => new Date(), []);

  // Все пресеты (фиксированные + кастомные)
  const allPresets = useMemo(
    () => [...FIXED_PRESETS, ...customPresets],
    [customPresets, FIXED_PRESETS]
  );

  // Превью следующих 3 номеров (без инкремента)
  const preview = useMemo(() => {
    const items: string[] = [];
    for (let i = 1; i <= 3; i++) {
      const num = renderTemplate(template, {
        date: today,
        customer,
        project,
        seq: i,
      });
      items.push(num);
    }
    return items;
  }, [template, today, customer, project]);

  const handleApply = (tpl?: string) => {
    const targetTemplate = typeof tpl === "string" ? tpl : template;
    // Определяем scope по наличию токенов в шаблоне
    let scope = "global";
    if (targetTemplate.includes("{customer}")) scope = "customer";
    else if (targetTemplate.includes("{YYYY}") || targetTemplate.includes("{YY}"))
      scope = "month";

    const previewFirst = renderTemplate(targetTemplate, {
      date: today,
      customer,
      project,
      seq: 1,
    });

    onApply?.(targetTemplate, scope, previewFirst);

    // Показать флэш "Пресет применён"
    const badge = document.getElementById("preset-badge");
    if (badge) {
      badge.textContent = texts.presetApplied;
      badge.className =
        "inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-xl bg-indigo-100 text-indigo-700 border border-indigo-200 opacity-100 transition-all shadow-sm";
      setTimeout(() => {
        badge.classList.add("opacity-0");
      }, 3000);
    }
  };

  const handleSave = async () => {
    try {
      // Сохраняем пресет на бэкенд
      const response = await authenticatedFetch(
        "/api/invoice-templates/presets",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ template }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        alert(`Ошибка: ${error.error}`);
        return;
      }

      // Определяем scope по наличию токенов в шаблоне
      let scope = "global";
      if (template.includes("{customer}")) scope = "customer";
      else if (template.includes("{YYYY}") || template.includes("{YY}"))
        scope = "month";

      // Локально добавим в список "Мои пресеты"
      // (внешний список обновится отдельно, но пользователь увидит результат сразу)
      try {
        // этот компонент не хранит локальный список пропсов — просто вызовем onSave
      } catch {}

      onSave(template, scope, preview[0]);
      // Показать флэш "Пресет сохранён" / "Preset saved" / "Předvolba uložena" на 7 сек с плавным исчезновением
      const badge = document.getElementById("preset-badge");
      if (badge) {
        badge.textContent = texts.presetSaved;
        badge.className =
          "inline-flex items-center gap-2 text-sm px-2 py-1 rounded-lg bg-green-100 text-green-700 border border-green-200 opacity-100 transition-opacity";
        setTimeout(() => {
          badge.classList.add("opacity-0");
        }, 7000);
      }
    } catch (error) {
      console.error("Error saving preset:", error);
      alert("Error saving preset");
    }
  };

  const handleDelete = async (templateToDelete: string) => {
    try {
      if (onDeletePreset) {
        await onDeletePreset(templateToDelete);
      }
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting preset:", error);
    }
  };

  function insertToken(token: string) {
    setTemplate((t) => {
      if (!t) return token;
      const sep =
        t.endsWith("-") || t.endsWith("/") ? "" : t.endsWith(" ") ? "" : "-";
      return t + sep + token;
    });
  }

  const invalidTokens = useMemo(() => {
    const allowed = new Set(BASE_TOKENS.concat(["{seq:"]));
    const matches = template.match(/\{[^}]+\}/g) || [];
    return matches.filter((m) => {
      if (m.startsWith("{seq:")) return !/^\{seq:\d+\}$/.test(m);
      return !allowed.has(m);
    });
  }, [template]);

  // Fallback to глобальный дефолтный шаблон с бэкенда при открытии модалки.
  // Это гарантия, что даже если локальный стейт пуст, у пользователя всегда будет валидное значение.
  // Бэкенд отдаёт fallback через /api/settings.invoiceNumberTemplate
  React.useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const resp = await authenticatedFetch("/api/settings", {
          ttl: 60000,
          cacheKey: "user-settings",
        });
        if (resp.ok) {
          const data = await resp.json();
          if (!template) {
            setTemplate(data.invoiceNumberTemplate || "YYMMDDNN");
          }
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Removed 'template' from dependencies to avoid API calls on every keystroke

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 transition-all"
      style={{ zIndex }}
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            {texts.title}
          </h2>
          <span
            id="preset-badge"
            className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-xl border opacity-0 transition-all duration-300 shadow-sm"
          ></span>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Левая колонка - настройки */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                {texts.templateLabel}
              </label>
              <input
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                value={template}
                onChange={(e) => setTemplate(sanitizeInput(e.target.value))}
                placeholder="INV-{YYYY}-{MM}-{seq:4}"
              />
              {invalidTokens.length > 0 && (
                <div className="mt-2 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 text-sm flex items-start gap-2">
                  <svg
                    className="w-5 h-5 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span>
                    {texts.invalidTokensPrefix}: {invalidTokens.join(", ")}
                  </span>
                </div>
              )}
            </div>

            <div>
              <div className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                {texts.quickTokens}
              </div>
              <div className="flex flex-wrap gap-2">
                {TOKEN_HELP.map((t) => (
                  <button
                    key={t.token}
                    className="text-xs font-medium rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition-all shadow-sm"
                    title={t.desc}
                    onClick={() => insertToken(t.token)}
                  >
                    {t.token}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                {texts.presets}
              </div>
              <div className="flex flex-wrap gap-2">
                {FIXED_PRESETS.map((p) => (
                  <button
                    key={p.value}
                    className={`group relative text-xs font-medium rounded-xl border px-4 py-2 transition-all shadow-sm flex items-center gap-2 ${
                      template === p.value
                        ? "border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
                        : "border-indigo-100 dark:border-indigo-900/30 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
                    }`}
                    onClick={() => {
                      setTemplate(p.value);
                      handleApply(p.value);
                    }}
                  >
                    {p.name}
                    {template === p.value && <Check size={12} />}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                {texts.myPresets}
              </div>
              <div className="space-y-3">
                {allPresets
                  .filter((p) => !p.isFixed)
                  .map((p) => (
                    <div
                      key={p.value}
                      className="flex items-center gap-2 group"
                    >
                      <button
                        className={`flex-1 text-xs font-medium text-left rounded-xl border px-4 py-3 transition-all shadow-sm flex items-center justify-between ${
                          template === p.value
                            ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                            : "border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900 hover:border-indigo-300 dark:hover:border-indigo-700"
                        }`}
                        onClick={() => {
                          setTemplate(p.value);
                          handleApply(p.value);
                        }}
                      >
                        <span>{p.name || p.value}</span>
                        {template === p.value && <Check size={14} />}
                      </button>
                      {onDeletePreset && (
                        <button
                          className="p-3 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                          onClick={() => setShowDeleteConfirm(p.value)}
                          title={texts.deletePresetTooltip}
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  ))}
                {customPresets.length === 0 && (
                  <div className="text-sm text-slate-500 italic py-2">
                    {texts.noCustomPresets}
                  </div>
                )}
              </div>
            </div>

            <div className="pt-6 grid grid-cols-2 gap-4">
              <button
                onClick={() => handleApply()}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm shadow-lg shadow-indigo-500/25 transition-all active:scale-[0.98]"
              >
                <Play size={16} className="fill-current" />
                {texts.applyPreset}
              </button>
              <SaveButton
                onClick={handleSave}
                isSaving={false}
                text={texts.savePreset}
                className="w-full"
              />
            </div>
          </div>

          {/* Правая колонка - превью */}
          <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-6 border border-slate-100 dark:border-slate-800 shadow-inner h-fit">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-indigo-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              {texts.previewTitle}
            </h3>
            <ul className="space-y-4">
              {preview.map((p, idx) => (
                <li
                  key={idx}
                  className="font-mono text-base border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-sm"
                >
                  <span className="text-slate-400 dark:text-slate-500 mr-2 text-xs">
                    {idx + 1}.
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Модальное окно подтверждения удаления */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-60 p-4 transition-all">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-md w-full shadow-2xl border border-slate-200 dark:border-slate-700">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
              <svg
                className="w-6 h-6 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              {texts.deletePresetTitle}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-8 text-base">
              {texts.deletePresetConfirm}
            </p>
            <div className="flex gap-4">
              <button
                className="flex-1 px-6 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 font-bold transition-all"
                onClick={() => setShowDeleteConfirm(null)}
              >
                {texts.cancel}
              </button>
              <button
                className="flex-1 px-6 py-3 rounded-xl bg-red-600 text-white hover:bg-red-700 font-bold shadow-lg shadow-red-500/30 transition-all"
                onClick={() => {
                  handleDelete(showDeleteConfirm);
                }}
              >
                {texts.delete}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
